import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Download, RotateCw, WifiOff, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * Three tiny floating toasts rolled into one component:
 *
 *  1. `updateReady` — a new SW version is waiting to be activated.
 *  2. `installPrompt` — the browser fired `beforeinstallprompt`.
 *  3. `offline` — navigator.onLine just flipped to false.
 *
 * Mounted once at the app root. Each auto-dismisses on user action; the
 * install prompt remembers its dismissal for 14 days so we don't nag.
 */
const INSTALL_SNOOZE_KEY = 'rh_install_dismissed_until';
const SNOOZE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

function PwaPrompts() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error) {
      console.warn('[pwa] sw register failed', error);
    },
  });

  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' ? !navigator.onLine : false,
  );

  // Capture the install-prompt event so we can fire it on user click later.
  useEffect(() => {
    const onBeforeInstall = (e: Event) => {
      const snoozeUntil = Number(
        localStorage.getItem(INSTALL_SNOOZE_KEY) ?? '0',
      );
      if (Date.now() < snoozeUntil) return;
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () =>
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  // Connectivity toast.
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const onInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    await installEvent.userChoice;
    setInstallEvent(null);
  };

  const onDismissInstall = () => {
    localStorage.setItem(
      INSTALL_SNOOZE_KEY,
      String(Date.now() + SNOOZE_MS),
    );
    setInstallEvent(null);
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-40 flex flex-col items-center gap-2 px-4 sm:bottom-6">
      {offline && (
        <Toast
          icon={<WifiOff className="size-4" aria-hidden />}
          title="You're offline"
          body="Cached content still works. New updates show up when you're back."
          tone="warn"
        />
      )}
      {needRefresh && (
        <Toast
          icon={<RotateCw className="size-4" aria-hidden />}
          title="A new version is ready"
          body="Reload to pick up the latest changes."
          tone="info"
          primaryLabel="Reload"
          onPrimary={() => void updateServiceWorker(true)}
          onDismiss={() => setNeedRefresh(false)}
        />
      )}
      {installEvent && (
        <Toast
          icon={<Download className="size-4" aria-hidden />}
          title="Install Dubs"
          body="Add to your home screen — opens full-screen, remembers your session."
          tone="info"
          primaryLabel="Install"
          onPrimary={() => void onInstall()}
          onDismiss={onDismissInstall}
        />
      )}
    </div>
  );
}

function Toast({
  icon,
  title,
  body,
  tone,
  primaryLabel,
  onPrimary,
  onDismiss,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  tone: 'info' | 'warn';
  primaryLabel?: string;
  onPrimary?: () => void;
  onDismiss?: () => void;
}) {
  return (
    <div
      role="status"
      className={[
        'pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border p-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.5)] backdrop-blur',
        tone === 'warn'
          ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-dim)]/95 text-on-surface'
          : 'border-[color:var(--color-border-strong)] bg-surface-2/95 text-on-surface',
      ].join(' ')}
    >
      <span
        className={[
          'mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full',
          tone === 'warn'
            ? 'bg-accent text-[color:var(--color-accent-ink)]'
            : 'bg-primary text-[color:var(--color-primary-ink)]',
        ].join(' ')}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-on-surface-dim">{body}</p>
      </div>
      {primaryLabel && onPrimary && (
        <button
          type="button"
          onClick={onPrimary}
          className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-[color:var(--color-primary-ink)] transition hover:bg-secondary"
        >
          {primaryLabel}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex size-7 shrink-0 items-center justify-center rounded-full text-on-surface-muted transition hover:bg-surface-3 hover:text-on-surface"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      )}
    </div>
  );
}

export default PwaPrompts;
